/**
 * Shared AFM™ rate + payment pricing simulator.
 *
 * Used by:
 *   - /market/portal/resources (investor portal)
 *   - /underwriting (warehouse / capital-markets reviewer portal)
 *
 * RatePricingSimulator — interactive: purchase price, down %, FICO →
 *   indicative rate, monthly P&I, tax, insurance, total.
 *
 * 🔑 THE RATE COMES FROM THE PRICING ENGINE (Hannah, 2026-11-03). It was a hard-coded FICO ladder (9.25%–10.00%) that
 * mirrored nothing Atlas prices on. Now GET /api/fee-schedule/illustrative-rate?price=&downPct=&fico= — the prime Atlas
 * prices on (the last known good rate when it is out of date) plus the one margin and Atlas's own FICO × LTV and loan-size
 * adjustments (backend utils/atlas indicativeRate). No prime on record → no number, the approved line instead. DTI and
 * file-specific adjustments come with a decision. Production loans still price through Atlas server-side.
 *
 * PMI removed from the product — non-QM loans can't bind MI. The PMI
 * column / matrix / pill were dropped; total monthly is P&I + Tax +
 * Insurance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useEstimateRates, pctOf } from '../constants/estimateRates';
// 🔑 Texas only (Hannah, 2026-11-03): the state is the licensed list, read from the licence mirror (this typed "(TX/FL avg)").
// The tax figure itself is now the Loan Estimate's served basis (constants/estimateRates), no longer the local mirror.
import { licensedStatesShort } from '../constants/marketExpansion';

const PRIMARY = '#3a6ad9';

const fmt$ = (n) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n, d = 2) => n == null ? '—' : `${(Number(n) * 100).toFixed(d)}%`;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Stat = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: '#111111' }}>{value}</div>
  </div>
);

export default function RatePricingSimulator() {
  const [purchasePrice, setPurchasePrice] = useState(500000);
  const [downPct, setDownPct] = useState(20);
  const [fico, setFico] = useState(700);

  // The engine's rate for these inputs: { rate } (a fraction), or none with the line (no prime) / ineligible.
  const [quote, setQuote] = useState({ loading: true, rate: null, line: null });
  // The Loan Estimate's tax / insurance basis, served.
  const estimateRates = useEstimateRates();
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      const dpq = Math.max(10, Math.min(35, Number(downPct) || 20));
      fetch(`${API_URL}/api/fee-schedule/illustrative-rate?price=${encodeURIComponent(Math.max(0, Number(purchasePrice) || 0))}&downPct=${encodeURIComponent(dpq)}&fico=${encodeURIComponent(Number(fico) || 0)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (alive) setQuote({ loading: false, rate: d && Number(d.rate) > 0 ? Number(d.rate) : null, line: d ? d.line : null }); })   // the server's approved line (utils/primeEstimate) — no second copy here
        .catch(() => { if (alive) setQuote({ loading: false, rate: null, line: null }); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [purchasePrice, downPct, fico]);

  const calc = useMemo(() => {
    const price = Math.max(0, Number(purchasePrice) || 0);
    const dp = Math.max(0.10, Math.min(0.35, Number(downPct) / 100 || 0.20));
    const loanAmount = Math.round(price * (1 - dp));
    const ltv = price > 0 ? loanAmount / price : 0;
    const rate = quote.rate;
    const n = 360;
    const monthlyRate = rate != null ? rate / 12 : 0;
    const piti = rate != null && loanAmount > 0 && monthlyRate > 0
      ? (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n))
      : null;
    // The Loan Estimate's basis, served: shares of the PRICE a year (constants/estimateRates; was 1.75% of price, 0.6% of the loan).
    const monthlyTax = estimateRates ? (price * estimateRates.tax) / 12 : null;
    const monthlyIns = estimateRates ? (price * estimateRates.hoi) / 12 : null;
    const total = piti != null && monthlyTax != null ? piti + monthlyTax + monthlyIns : null;
    return { price, dp, loanAmount, ltv, rate, piti, monthlyTax, monthlyIns, total };
  }, [purchasePrice, downPct, quote.rate, estimateRates]);

  const ltvColor = calc.ltv <= 0.80 ? '#111111' : calc.ltv <= 0.90 ? '#111111' : '#ff082f';
  const ltvBg    = calc.ltv <= 0.80 ? '#f0fde4' : calc.ltv <= 0.90 ? '#fffde4' : '#fff5f6';

  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'block' };
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111111', boxSizing: 'border-box', outline: 'none' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 16, marginBottom: 18 }}>
        <div>
          <label style={labelStyle}>Purchase Price</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14, pointerEvents: 'none' }}>$</span>
            <input type="number" min={50000} step={5000} value={purchasePrice}
              onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)}
              style={{ ...inputStyle, paddingLeft: 22 }} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Down Payment %</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="range" min={10} max={35} step={1} value={downPct}
              onChange={(e) => setDownPct(Number(e.target.value))}
              style={{ flex: 1, accentColor: PRIMARY }} />
            <input type="number" min={10} max={35} step={1} value={downPct}
              onChange={(e) => setDownPct(Number(e.target.value))}
              style={{ ...inputStyle, width: 80 }} />
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>%</span>
          </div>
        </div>
        <div>
          <label style={labelStyle}>FICO Score</label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="range" min={560} max={800} step={5} value={fico}
              onChange={(e) => setFico(Number(e.target.value))}
              style={{ flex: 1, accentColor: PRIMARY }} />
            <input type="number" min={560} max={800} step={5} value={fico}
              onChange={(e) => setFico(Number(e.target.value))}
              style={{ ...inputStyle, width: 90 }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Loan Amount</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111111' }}>{fmt$(calc.loanAmount)}</div>
        </div>
        <div style={{ background: ltvBg, border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>LTV</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: ltvColor }}>{(calc.ltv * 100).toFixed(1)}%</div>
        </div>
      </div>

      <div style={{ background: '#eff3fe', border: '1px solid #bfdbfe', borderRadius: 12, padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px), 1fr))', gap: 14, marginBottom: 14 }}>
          <Stat label="Indicative Rate" value={fmtPct(calc.rate, 3)} />
          <Stat label="Monthly P&I" value={calc.piti == null ? '—' : fmt$(Math.round(calc.piti))} />
          <Stat label="Monthly Tax (est.)" value={fmt$(Math.round(calc.monthlyTax))} />
          <Stat label="Monthly Ins. (est.)" value={fmt$(Math.round(calc.monthlyIns))} />
        </div>
        <div style={{ borderTop: '1px solid #bfdbfe', paddingTop: 12, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Est. Monthly</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: '#111111' }}>{calc.total == null ? '—' : fmt$(Math.round(calc.total))}</span>
        </div>
      </div>

      {!quote.loading && quote.rate == null && quote.line && (
        <p data-testid="rate-simulator-rate-pending" style={{ margin: '0 0 8px', fontSize: 13, color: '#111111' }}>{quote.line}</p>
      )}
      <p style={{ margin: 0, fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
        Rates shown are indicative only based on current Doorly AFM™ program parameters. Actual rate and terms are subject to credit review and program eligibility. Not a commitment to lend. Tax estimate based on {pctOf(estimateRates && estimateRates.tax)} of price ({licensedStatesShort()}). Insurance estimate based on {pctOf(estimateRates && estimateRates.hoi)} of price.
      </p>
    </div>
  );
}
